import { type FC, type MouseEvent, type ReactNode, useCallback, useEffect } from 'react';
import { graphql, useFragment, useRelayEnvironment, fetchQuery } from 'react-relay';
import dibsCss from 'dibs-css';
import { trackEvent } from 'dibs-tracking';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import contactDealerLauncher from 'dibs-buyer-layout/exports/contactDealerLauncher.jsx';
import { BuyerIncentivesBannerCompactLazy } from 'dibs-buyer-incentives/exports/BuyerIncentivesBannerCompactLazy.ts';
import { SellerAwayBanner } from 'dibs-presence/exports/SellerAwayBanner.ts';
import { getQueryParam } from '../utils/uriUtils.ts';

import { type SharedContactDealerWrapper_item$key } from './__generated__/SharedContactDealerWrapper_item.graphql.ts';
import { type SharedContactDealerWrapper_user$key } from './__generated__/SharedContactDealerWrapper_user.graphql.ts';
import { type SharedContactDealerWrapperRefetchQuery } from './__generated__/SharedContactDealerWrapperRefetchQuery.graphql.ts';

const itemFragment = graphql`
    fragment SharedContactDealerWrapper_item on Item {
        serviceId
        seller {
            ...SellerAwayBanner_seller
        }
    }
`;

const userFragment = graphql`
    fragment SharedContactDealerWrapper_user on User {
        serviceId
    }
`;

/* eslint-disable relay/unused-fields -- refetch intentionally primes store for sibling shipping UI */
const refetchQueryNode = graphql`
    query SharedContactDealerWrapperRefetchQuery(
        $userId: String!
        $itemId: String!
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $fetchUser: Boolean!
        $itemQuantity: Int
    ) {
        viewer {
            ...PdpSharedShippingInfoLink_viewer
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
            ) {
                quoteRequestStatus
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    handlingTimeDisplay(displayShort: true)
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */
export type ModalType =
    | 'CONTACT_DEALER'
    | 'REQUEST_CUSTOMIZATION'
    | 'REQUEST_PRICE'
    | 'REQUEST_IMAGES'
    | 'REQUEST_IMAGES_AND_VIDEOS'
    | 'DESIGN_YOUR_OWN';

type Props = {
    item: SharedContactDealerWrapper_item$key;
    user?: SharedContactDealerWrapper_user$key | null;
    selectedSkuId?: string;
    launchOrigin?: string;
    loadOnMount?: boolean;
    modalType?: ModalType;
    currency: string;
    itemQuantity?: number;
    showSellerBranding?: boolean;
    showSellerAwayBanner?: boolean;
    children: (onClick: (event?: MouseEvent | null) => void) => ReactNode;
};

export const SharedContactDealerWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    selectedSkuId,
    launchOrigin = '',
    loadOnMount = false,
    modalType = 'CONTACT_DEALER',
    currency,
    itemQuantity,
    showSellerBranding,
    showSellerAwayBanner,
    children,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef ?? null);

    const doRefetch = useCallback(
        (refetchVariables: Record<string, unknown>) => {
            const variables = {
                ...refetchVariables,
                userCountryCode: getUserSessionCountryCode(),
                itemQuantity,
            };
            fetchQuery<SharedContactDealerWrapperRefetchQuery>(
                environment,
                refetchQueryNode,
                variables as SharedContactDealerWrapperRefetchQuery['variables']
            ).subscribe({});
        },
        [environment, itemQuantity]
    );

    const onContactClick = useCallback(
        (event?: MouseEvent | null) => {
            trackEvent(
                {
                    category: 'contact dealer',
                    action: 'contact dealer clicked',
                    label: launchOrigin,
                    eventName: 'contact',
                    step_interaction_name: 'entry',
                    interaction_type: 'contact dealer',
                    trigger: launchOrigin,
                },
                event
            );

            let infoBanner;
            if (showSellerAwayBanner) {
                infoBanner = (
                    <SellerAwayBanner
                        seller={item.seller}
                        messageType="regular"
                        trackingTrigger="ask a seller modal"
                    />
                );
            } else if (showSellerBranding) {
                infoBanner = (
                    <BuyerIncentivesBannerCompactLazy wrapperClassName={dibsCss.roundedXxsmall} />
                );
            }

            contactDealerLauncher({
                environment,
                itemId: item?.serviceId,
                selectedSkuId,
                launchOrigin,
                modalType,
                currency,
                userId: user?.serviceId || null,
                onSuccessfulShippingQuoteRequest: doRefetch,
                itemQuantity,
                showSellerBranding,
                infoBanner,
            });
        },
        [
            environment,
            item,
            user,
            selectedSkuId,
            launchOrigin,
            modalType,
            currency,
            itemQuantity,
            doRefetch,
            showSellerBranding,
            showSellerAwayBanner,
        ]
    );

    useEffect(() => {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        if (
            loadOnMount ||
            (modalType === 'CONTACT_DEALER' && modalParam === 'contact-dealer') ||
            (modalType === 'REQUEST_CUSTOMIZATION' && modalParam === 'request-customization')
        ) {
            onContactClick(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div data-tn="contact-dealer-wrapper-cta">{children(onContactClick)}</div>;
};
